

import { useCallback, useMemo, useState } from "react"
import { DollarSign, FileText, Loader2, RefreshCw, Trash2, Filter, FileArchive, FileCog } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ActionFileUpload } from "./action-file-upload"
import { useToast } from "@/lib/hooks/use-toast"
import {
  useFiltrarS3000,
  useGerarExcludente,
  useGerarS3000S1210,
  useGerarXmlCorrigido,
  useLimparXmlPrevidenciario,
  useRestaurarRubrica,
  useRetificarVerbas,
} from "@/features/products/services"
import { isDownloadFileResult, triggerDownloadFile } from "@/features/products/services/download-utils"

type QuickActionId =
  | "restaurar_rubrica"
  | "gerar_exclusao"
  | "retificar_verba"
  | "limpar_xml"
  | "filtrar_s3000"
  | "gerar_s3000_s1210"
  | "gerar_xml_corrigido"

interface QuickActionConfig {
  id: QuickActionId
  label: string
  description: string
  submitLabel: string
  icon: React.ComponentType<{ className?: string }>
}

interface QuickActionHistory {
  sentAt: Date
  files: string[]
  status: "success" | "error"
  message?: string
}

const QUICK_ACTIONS: QuickActionConfig[] = [
  {
    id: "restaurar_rubrica",
    label: "Restaurar Rubrica",
    description: "Restaura rubricas com base no arquivo enviado.",
    submitLabel: "Enviar para restaurar",
    icon: RefreshCw,
  },
  {
    id: "gerar_exclusao",
    label: "Gerar Exclusao",
    description: "Gera a exclusao a partir do arquivo enviado.",
    submitLabel: "Enviar para gerar exclusao",
    icon: FileText,
  },
  {
    id: "retificar_verba",
    label: "Retificar Verba",
    description: "Retifica verbas com base no arquivo enviado.",
    submitLabel: "Enviar para retificar verba",
    icon: DollarSign,
  },
  {
    id: "limpar_xml",
    label: "Limpar XML",
    description: "Limpa XMLs a partir do arquivo enviado.",
    submitLabel: "Enviar para limpar XML",
    icon: Trash2,
  },
  {
    id: "filtrar_s3000",
    label: "Filtrar S-3000",
    description: "Filtra eventos S-3000 do arquivo informado.",
    submitLabel: "Enviar para filtrar S-3000",
    icon: Filter,
  },
  {
    id: "gerar_s3000_s1210",
    label: "Gerar S-3000 S-1210",
    description: "Gera eventos S-3000 com base no S-1210.",
    submitLabel: "Enviar para gerar S-3000",
    icon: FileArchive,
  },
  {
    id: "gerar_xml_corrigido",
    label: "Gerar XML Corrigido",
    description: "Gera XML corrigido para reprocessamento.",
    submitLabel: "Enviar para gerar XML corrigido",
    icon: FileCog,
  },
]

const INITIAL_FILES: Record<QuickActionId, File[]> = {
  restaurar_rubrica: [],
  gerar_exclusao: [],
  retificar_verba: [],
  limpar_xml: [],
  filtrar_s3000: [],
  gerar_s3000_s1210: [],
  gerar_xml_corrigido: [],
}

const INITIAL_HISTORY: Record<QuickActionId, QuickActionHistory | null> = {
  restaurar_rubrica: null,
  gerar_exclusao: null,
  retificar_verba: null,
  limpar_xml: null,
  filtrar_s3000: null,
  gerar_s3000_s1210: null,
  gerar_xml_corrigido: null,
}

function formatDateTime(date: Date): string {
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return "Erro ao executar acao."
}

interface QuickActionsPanelProps {
  idPrevidenciario?: string
}

export function QuickActionsPanel({ idPrevidenciario }: QuickActionsPanelProps) {
  const { toast } = useToast()
  const [selectedActionId, setSelectedActionId] = useState<QuickActionId | null>(null)
  const [currentActionId, setCurrentActionId] = useState<QuickActionId | null>(null)
  const [filesByAction, setFilesByAction] = useState<Record<QuickActionId, File[]>>(INITIAL_FILES)
  const [historyByAction, setHistoryByAction] = useState<Record<QuickActionId, QuickActionHistory | null>>(INITIAL_HISTORY)

  const restaurarRubricaMutation = useRestaurarRubrica()
  const gerarExcludenteMutation = useGerarExcludente()
  const retificarVerbasMutation = useRetificarVerbas()
  const limparXmlMutation = useLimparXmlPrevidenciario()
  const filtrarS3000Mutation = useFiltrarS3000()
  const gerarS3000S1210Mutation = useGerarS3000S1210()
  const gerarXmlCorrigidoMutation = useGerarXmlCorrigido()

  const selectedAction = useMemo(
    () => QUICK_ACTIONS.find((action) => action.id === selectedActionId) ?? null,
    [selectedActionId]
  )

  const selectedFiles = selectedAction ? filesByAction[selectedAction.id] : []
  const currentHistory = selectedAction ? historyByAction[selectedAction.id] : null
  const isSending = selectedAction ? currentActionId === selectedAction.id : false
  const isBusy = currentActionId !== null

  const executeAction = useCallback(
    async (actionId: QuickActionId) => {
      const files = filesByAction[actionId]
      const file = files[0]
      if (!file) return

      setCurrentActionId(actionId)

      try {
        switch (actionId) {
          case "restaurar_rubrica":
            triggerDownloadFile(await restaurarRubricaMutation.mutateAsync({ file, responseFormat: "zip" }))
            break
          case "gerar_exclusao":
            triggerDownloadFile(await gerarExcludenteMutation.mutateAsync({ file }))
            break
          case "retificar_verba":
            triggerDownloadFile(await retificarVerbasMutation.mutateAsync({ file }))
            break
          case "limpar_xml":
            triggerDownloadFile(await limparXmlMutation.mutateAsync({ file }))
            break
          case "filtrar_s3000":
            triggerDownloadFile(await filtrarS3000Mutation.mutateAsync({ file, includeS3000: false }))
            break
          case "gerar_s3000_s1210":
            triggerDownloadFile(await gerarS3000S1210Mutation.mutateAsync({ file }))
            break
          case "gerar_xml_corrigido":
            {
              const response = await gerarXmlCorrigidoMutation.mutateAsync({
                file,
                responseFormat: "zip",
                idPrevidenciario: idPrevidenciario || "",
              })

              if (!isDownloadFileResult(response)) {
                throw new Error("Resposta inválida ao gerar XML corrigido.")
              }

              triggerDownloadFile(response)
            }
            break
          default:
            break
        }

        setHistoryByAction((prev) => ({
          ...prev,
          [actionId]: {
            sentAt: new Date(),
            files: files.map((sentFile) => sentFile.name),
            status: "success",
          },
        }))
        setFilesByAction((prev) => ({ ...prev, [actionId]: [] }))
        toast({ description: "Acao executada com sucesso." })
      } catch (error) {
        const message = getErrorMessage(error)
        setHistoryByAction((prev) => ({
          ...prev,
          [actionId]: {
            sentAt: new Date(),
            files: files.map((sentFile) => sentFile.name),
            status: "error",
            message,
          },
        }))
        toast({ variant: "destructive", description: message })
      } finally {
        setCurrentActionId(null)
      }
    },
    [
      filesByAction,
      restaurarRubricaMutation,
      gerarExcludenteMutation,
      retificarVerbasMutation,
      limparXmlMutation,
      filtrarS3000Mutation,
      gerarS3000S1210Mutation,
      gerarXmlCorrigidoMutation,
      toast,
    ]
  )

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-foreground break-words">Ações Rapidas</h2>
        <p className="text-xs text-muted-foreground break-words">
          Escolha uma acao para enviar arquivos ZIP ou XML.
        </p>
        {idPrevidenciario && (
          <p className="text-xs text-muted-foreground break-words">Apuracao vinculada: {idPrevidenciario}</p>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {QUICK_ACTIONS.map((action) => {
          const Icon = action.icon
          const isSelected = selectedActionId === action.id
          return (
            <Button
              key={action.id}
              variant={isSelected ? "default" : "outline"}
              className="h-auto min-h-0 items-start justify-start gap-2 py-3 text-left whitespace-normal"
              onClick={() => setSelectedActionId(action.id)}
              disabled={isBusy}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium break-words">{action.label}</span>
                <span className="block text-xs opacity-80 break-words">{action.description}</span>
              </span>
            </Button>
          )
        })}
      </div>

      {selectedAction && (
        <div className="mt-4 space-y-3 rounded-lg border border-border/70 bg-background p-3">
          <div>
            <p className="text-sm font-medium text-foreground break-words">{selectedAction.label}</p>
            <p className="text-xs text-muted-foreground break-words">{selectedAction.description}</p>
          </div>

          <ActionFileUpload
            files={selectedFiles}
            onFilesChange={(newFiles) =>
              setFilesByAction((prev) => ({ ...prev, [selectedAction.id]: newFiles }))
            }
            label="Envie ZIP ou XML para processar esta acao"
            allowMultiple={false}
          />

          <Button
            className="w-full"
            disabled={isSending || selectedFiles.length === 0}
            onClick={() => executeAction(selectedAction.id)}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              selectedAction.submitLabel
            )}
          </Button>

          {currentHistory ? (
            <p className="text-xs text-muted-foreground break-words">
              Ultimo envio em {formatDateTime(currentHistory.sentAt)} | Arquivos: {currentHistory.files.join(", ")} | Status:{" "}
              {currentHistory.status === "success" ? "sucesso" : `erro (${currentHistory.message || "falha"})`}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground break-words">Nenhum envio realizado para esta acao.</p>
          )}
        </div>
      )}
    </section>
  )
}
